import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  alpha,
  useTheme,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../theme';

const InvitationPortal = ({ invitationToken, invitedEmail, invitedCompany, invitedRole, onSuccess }) => {
  const muiTheme = useTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [userExists, setUserExists] = useState(false); // Always default to false - let user choose
  const [mode, setMode] = useState('choose'); // 'choose', 'signin', 'signup'

  useEffect(() => {
    // Always use company and role from URL if available, no database query needed
    if (invitedCompany && invitedRole) {
      setInvitationDetails({
        companies: { name: invitedCompany },
        roles: { display_name: invitedRole }
      });
    }
    
    // No need to check if user exists - just show both options and let them choose
    setUserExists(false);
  }, [invitedCompany, invitedRole, invitedEmail]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate password
      if (!password) {
        throw new Error('Please enter your password');
      }

      // Sign in with existing account
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitedEmail,
        password: password
      });

      if (signInError) {
        throw new Error('Invalid password. Please check your password and try again.');
      }

      // User signed in successfully, accept the invitation
      const { error: inviteError } = await supabase
        .rpc('accept_invitation', { p_invitation_token: invitationToken });

      if (inviteError) {
        console.error('Invitation acceptance error:', inviteError);
        throw new Error('Failed to accept invitation: ' + inviteError.message);
      }

      // SECURITY FIX: Clear ALL cached company data before redirect
      localStorage.removeItem('currentCompanyId');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('pendingInvitationToken');
      localStorage.removeItem('pendingInvitationEmail');
      localStorage.removeItem('pendingInvitationCompany');
      localStorage.removeItem('pendingInvitationRole');
      
      // SECURITY FIX: Clear URL to prevent old company from showing
      window.history.replaceState({}, document.title, '/');
      
      setSuccess('Successfully joined the company! Redirecting...');
      setTimeout(() => {
        // Use onSuccess callback instead of reload
        onSuccess();
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate passwords
      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Try to create new account directly via edge function
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/confirm-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabase.supabaseKey}`
        },
        body: JSON.stringify({
          email: invitedEmail,
          password: password,
          invitation_token: invitationToken,
          company_name: invitedCompany,
          user_name: invitedEmail.split('@')[0]
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // Check if error is because user already exists
        if (result.error && (result.error.includes('already registered') || result.error.includes('User already registered'))) {
          throw new Error('An account with this email already exists. Please click "I already have an account" above and sign in with your existing password.');
        }
        throw new Error(result.error || 'Failed to create account');
      }

      setSuccess(`Account created! Check your email for a confirmation link. After confirming, sign in to join ${invitedCompany}.`);
      localStorage.setItem('pendingInvitationToken', invitationToken);
      localStorage.setItem('pendingInvitationEmail', invitedEmail);
      localStorage.setItem('pendingInvitationCompany', invitedCompany);
      localStorage.setItem('pendingInvitationRole', invitedRole);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (!invitationDetails && !invitedCompany) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f7f7f8',
            px: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: 500,
              width: '100%',
              borderRadius: 4,
              border: `1px solid ${alpha('#000', 0.1)}`
            }}
          >
            <Typography variant="h5" sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}>
              Loading invitation...
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f7f7f8',
          position: 'relative'
        }}
      >
        {/* GlowWorks logo */}
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            GlowWorks
          </Typography>
        </Box>

        {/* Main content */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
          <Paper
            elevation={0}
            sx={{
              p: 6,
              maxWidth: 500,
              width: '100%',
              borderRadius: 4,
              border: `1px solid ${alpha('#000', 0.1)}`,
              textAlign: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                transform: 'translateY(-2px)',
                border: `1px solid ${alpha('#000', 0.15)}`
              }
            }}
          >
            {/* Welcome message */}
            <Box sx={{ mb: 4 }}>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 60, 
                  color: '#10a37f', 
                  mb: 2
                }} 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 500,
                  color: '#000',
                  fontSize: '2rem',
                  mb: 2,
                  letterSpacing: '-0.5px'
                }}
              >
                You're Invited!
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: alpha('#000', 0.7),
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 1
                }}
              >
                You've been invited to join <strong>{invitedCompany || invitationDetails?.companies?.name || 'a company'}</strong>
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha('#000', 0.6),
                  fontSize: '0.95rem'
                }}
              >
                Role: {invitedRole || invitationDetails?.roles?.display_name || 'Team Member'}
              </Typography>
            </Box>

            {/* Mode Selection or Form */}
            <Box sx={{ textAlign: 'left' }}>
              {mode === 'choose' && (
                <>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: '#000',
                      fontSize: '1.2rem',
                      mb: 3,
                      textAlign: 'center'
                    }}
                  >
                    Join the company
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setMode('signin')}
                      sx={{
                        py: 2,
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 4,
                        border: `2px solid ${alpha('#000', 0.2)}`,
                        color: '#000',
                        '&:hover': {
                          border: `2px solid #000`,
                          backgroundColor: alpha('#000', 0.05)
                        }
                      }}
                    >
                      I already have an account
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => userExists ? null : setMode('signup')}
                      disabled={userExists === true}
                      sx={{
                        py: 2,
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 4,
                        backgroundColor: userExists === true ? alpha('#000', 0.3) : '#000',
                        color: userExists === true ? alpha('#fff', 0.5) : '#fff',
                        cursor: userExists === true ? 'not-allowed' : 'pointer',
                        '&:hover': {
                          backgroundColor: userExists === true ? alpha('#000', 0.3) : alpha('#000', 0.8)
                        },
                        '&:disabled': {
                          backgroundColor: alpha('#000', 0.3),
                          color: alpha('#fff', 0.5)
                        }
                      }}
                    >
                      {userExists === true ? 'Account already exists' : 'Create new account'}
                    </Button>
                  </Box>
                  
                  {userExists === true && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: alpha('#000', 0.6),
                        fontSize: '0.9rem',
                        textAlign: 'center',
                        mb: 2,
                        fontStyle: 'italic'
                      }}
                    >
                      An account with this email already exists. Please sign in above.
                    </Typography>
                  )}
                </>
              )}

              {(mode === 'signin' || mode === 'signup') && (
                <>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: '#000',
                      fontSize: '1.2rem',
                      mb: 3,
                      textAlign: 'center'
                    }}
                  >
                    {mode === 'signin' ? 'Sign in to join' : 'Create your password'}
                  </Typography>

                  {/* Error/Success Messages */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      {success}
                    </Alert>
                  )}
                </>
              )}

              {(mode === 'signin' || mode === 'signup') && (
                <form onSubmit={mode === 'signin' ? handleSignIn : handleCreateAccount}>
                {/* Email display */}
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={invitedEmail}
                  disabled
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      backgroundColor: alpha('#000', 0.03)
                    }
                  }}
                />

                {/* Password field */}
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                        borderWidth: 1
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handlePasswordVisibility}
                          edge="end"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                {/* Confirm password field - only for signup */}
                {mode === 'signup' && (
                  <TextField
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    sx={{
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 4,
                        fontSize: '1rem',
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha('#000', 0.2)
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha('#000', 0.4)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#000',
                          borderWidth: 1
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleConfirmPasswordVisibility}
                            edge="end"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}

                {/* Add margin bottom if signin mode (no confirm password) */}
                {mode === 'signin' && <Box sx={{ mb: 4 }} />}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 4,
                    fontWeight: 500,
                    fontSize: '1rem',
                    textTransform: 'none',
                    backgroundColor: '#000',
                    color: '#fff',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: '#333'
                    },
                    '&:disabled': {
                      backgroundColor: alpha('#000', 0.3)
                    }
                  }}
                >
                  {loading 
                    ? (mode === 'signin' ? 'Signing In...' : 'Creating Account...')
                    : (mode === 'signin' ? 'Sign In & Join Company' : 'Create Account & Join Company')
                  }
                </Button>
                
                <Button
                  onClick={() => {
                    setMode('choose');
                    setError('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  fullWidth
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    borderRadius: 4,
                    fontWeight: 500,
                    fontSize: '1rem',
                    textTransform: 'none',
                    border: `2px solid ${alpha('#000', 0.2)}`,
                    color: '#000',
                    '&:hover': {
                      border: `2px solid #000`,
                      backgroundColor: alpha('#000', 0.05)
                    }
                  }}
                >
                  Back
                </Button>
              </form>
              )}
            </Box>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha('#000', 0.5),
                  fontSize: '0.85rem'
                }}
              >
                &copy; {new Date().getFullYear()} GlowWorks. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default InvitationPortal;